"use client";

// Register „Untergruppen“: derselbe Baum wie unter /gruppen, nur
// unterhalb dieser Gruppe. Archivierte Untergruppen stehen nicht im
// Baum, sondern als Hinweis darunter — mit dem Weg ins Archiv.
import { useState } from "react";
import Link from "next/link";
import { FolderTree, Plus } from "lucide-react";
import { Button, DialogAmRumpf, EmptyState } from "@/components/ui";
import { Fusstext, Listenblatt, Listenfuss, Listenkoerper } from "@/components/liste";
import { aktuellePersonen, baumZeilen, nachfahren } from "@/lib/gruppen/logik";
import type { GruppeView } from "@/lib/gruppen/views";
import { useGruppenBestand } from "./gruppen-bestand";
import { GruppenBaum } from "./gruppen-baum";
import { GruppeDialog } from "./gruppe-dialog";

export function GruppenUntergruppen({ id }: { id: string }) {
  const { gruppen, zugehoerigkeiten, heute, rechte } = useGruppenBestand();
  const [dialog, setDialog] = useState<null | { gruppe?: GruppeView; eltern?: string }>(null);
  const gruppe = gruppen.find((g) => g.id === id);
  if (!gruppe) return null;

  const unten = new Set(nachfahren(gruppen, id));
  const aktiv = gruppen.filter((g) => unten.has(g.id) && !g.isArchived);
  const archiviert = gruppen.filter((g) => unten.has(g.id) && g.isArchived);
  const zeilen = baumZeilen(aktiv, new Set(aktiv.map((g) => g.id)), (l) =>
    [...l].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "de")),
  );
  const personen = aktuellePersonen(zugehoerigkeiten, aktiv.map((g) => g.id), heute).size;
  const darf = rechte.schreiben && !gruppe.isArchived;

  return (
    <Listenblatt>
      {darf && zeilen.length > 0 && (
        <div className="border-border flex items-center gap-3 border-b px-4 py-2.5">
          <p className="text-body-sm text-text-secondary min-w-0 flex-1 truncate">
            Was unter „{gruppe.name}“ hängt, zählt dort mit — jede Person einmal.
          </p>
          <Button size="sm" variant="secondary" onClick={() => setDialog({ eltern: id })}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Untergruppe
          </Button>
        </div>
      )}

      {zeilen.length === 0 ? (
        <Listenkoerper className="p-5">
          <EmptyState
            icon={FolderTree}
            title="Keine Untergruppen"
            description={
              gruppe.kind === "DEPARTMENT"
                ? "Lege die Mannschaften dieser Abteilung an — sie zählen dann in der Abteilung mit."
                : "Teams, Arbeitsgruppen oder Jahrgänge lassen sich darunter ordnen."
            }
            action={
              darf ? (
                <Button onClick={() => setDialog({ eltern: id })}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Untergruppe anlegen
                </Button>
              ) : undefined
            }
          />
        </Listenkoerper>
      ) : (
        <GruppenBaum
          zeilen={zeilen}
          onBearbeiten={(g) => setDialog({ gruppe: g })}
          onUntergruppe={(g) => setDialog({ eltern: g.id })}
        />
      )}

      <Listenfuss>
        <Fusstext titel="Summe" zusatz={`${aktiv.length} ${aktiv.length === 1 ? "Untergruppe" : "Untergruppen"} · ${personen} Personen`} />
        {archiviert.length > 0 && (
          <Link href="/gruppen?zustand=archiv" className="text-body-sm text-text-secondary hover:text-accent">
            Archiviert: {archiviert.map((g) => g.name).join(", ")}
          </Link>
        )}
      </Listenfuss>

      <DialogAmRumpf>
        <GruppeDialog offen={dialog !== null} onClose={() => setDialog(null)} gruppe={dialog?.gruppe} vorgabeEltern={dialog?.eltern} />
      </DialogAmRumpf>
    </Listenblatt>
  );
}
